import {
  Box,
  Button,
  Grid,
  RoundedBox,
  Stack,
} from "@braintrust/braintrust-ui-components";
import { ArrowTopRightIcon } from "@braintrust/braintrust-ui-components/Icons";
import type { FreelancerLocationMismatch } from "@js/apps/admin/types";
import { isPublicBidWithoutUser } from "@js/apps/jobs/apps/bids/helpers";
import { RouterLink } from "@js/components/link";
import { LocationMismatchAlert } from "@js/components/location-mismatch-alert";
import type { User } from "@js/types/auth";
import type { EmployerBid, EmployerBidListBid, Job } from "@js/types/jobs";

import { BidActionsMenu } from "../bid-actions-menu";
import { BidCTAButton } from "../bid-cta-button";
import { ReactionsFeedbackField } from "../bid-reactions";

import { BidTabs } from "./tabs/bid-tabs";

import styles from "./right-panel.module.scss";

export type BidListRightPanelProps = {
  job: Job;
  bid: EmployerBidListBid | EmployerBid | undefined;
  closeDrawer: () => void;
  locationMismatchData?: Pick<
    FreelancerLocationMismatch,
    "last_active_session_mismatch" | "billing_location_mismatch"
  > & { user: Pick<User, "first_name"> };
};

export const BidListRightPanel = ({
  job,
  bid,
  closeDrawer,
  locationMismatchData,
}: BidListRightPanelProps) => {
  if (!bid || !job) return null;

  const hasProfile = !isPublicBidWithoutUser(bid);

  return (
    <Grid
      size={{
        xs: 12,
        sm: 8.5,
      }}
    >
      {!!locationMismatchData && (
        <LocationMismatchAlert
          userFirstName={locationMismatchData.user.first_name}
          sessionLocationMismatch={Boolean(
            locationMismatchData.last_active_session_mismatch,
          )}
          billingLocationMismatch={Boolean(
            locationMismatchData.billing_location_mismatch,
          )}
          className={styles.locationAlert}
        />
      )}
      <RoundedBox
        sx={{
          bgcolor: "var(--white)",
          overflow: "hidden",
          p: { xs: 3, sm: 4 },
          borderRadius: { xs: "0px", sm: "16px" },
          margin: { xs: "0 -24px", sm: "auto" },
          width: { xs: "calc(100% + 48px)", sm: "100%" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 2,
            whiteSpace: "nowrap",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box>
              <ReactionsFeedbackField bid={bid} isDrawer />
            </Box>
            {hasProfile && (
              <Box>
                <Button
                  variant="white-violet"
                  shape="squared"
                  to={bid.freelancer.full_link}
                  rel="noopener noreferrer"
                  endIcon={<ArrowTopRightIcon style={{ fontSize: 24 }} />}
                  target="_blank"
                  RouterLink={RouterLink}
                  size="x-small"
                  sx={{
                    height: "42px",
                  }}
                >
                  View profile
                </Button>
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            {hasProfile && (
              <Stack
                direction="row"
                data-testid="bid-cta-button"
                sx={{ gap: 1 }}
              >
                <BidCTAButton bid={bid} job={job} closeDrawer={closeDrawer} />
              </Stack>
            )}
            <Box>
              <BidActionsMenu bid={bid} job={job} closeDrawer={closeDrawer} />
            </Box>
          </Box>
        </Box>
        <Box sx={{ mb: 3, mt: "20px" }}>
          <BidTabs bid={bid} job={job} />
        </Box>
      </RoundedBox>
    </Grid>
  );
};
